import CustomTooltip from '../../shared/CustomTooltip';
import {
    Stack,
    Box,
    InputLabel,
    Typography,
    Grid,
    Switch,
    Paper,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import {
    useEditPlatformSettingsMutation,
    useGetPlatformSettingsQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import toast from '../../shared/Toast';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FormTextField from '../../shared/FormTextField';

const Schema = z.object({
    automaticCreditHold: z.boolean(),
    daysOverdueBeforeAutomaticCreditHold: z.coerce.number().min(1).max(30),
    automaticBookingCancellation: z.boolean(),
    daysBeforeBookingCancellation: z.coerce.number().min(1).max(14),
});
type CreditHoldSettings = z.infer<typeof Schema>;

const mapSettingsToFormValues = (
    settings: Partial<CreditHoldSettings> | undefined,
): CreditHoldSettings => ({
    automaticCreditHold: settings?.automaticCreditHold ?? false,
    daysOverdueBeforeAutomaticCreditHold: Number(
        settings?.daysOverdueBeforeAutomaticCreditHold ?? 5,
    ),
    automaticBookingCancellation:
        settings?.automaticBookingCancellation ?? false,
    daysBeforeBookingCancellation: Number(
        settings?.daysBeforeBookingCancellation ?? 5,
    ),
});

const CreditHoldSettings = () => {
    const { data: settings } =
        useGetPlatformSettingsQuery('/platform-settings');
    const [updateSettings] = useEditPlatformSettingsMutation();

    const methods = useForm<CreditHoldSettings>({
        resolver: zodResolver(Schema),
        mode: 'onChange',
        defaultValues: {
            automaticCreditHold: false,
            daysOverdueBeforeAutomaticCreditHold: 5,
            automaticBookingCancellation: false,
            daysBeforeBookingCancellation: 5,
        },
    });

    const { control, reset, handleSubmit } = methods;

    useEffect(() => {
        if (settings) {
            reset(mapSettingsToFormValues(settings));
        }
    }, [settings, reset]);

    const watchCreditHold = methods.watch('automaticCreditHold');
    const watchCancel = methods.watch('automaticBookingCancellation');

    const onSubmit = async (data: CreditHoldSettings) => {
        try {
            await updateSettings(data).unwrap();
            reset(mapSettingsToFormValues(data));
            toast.success('Credit hold saved successfully!');
        } catch {
            toast.error('Error updating credit hold permissions');
        }
    };

    return (
        <Grid container justifyContent='left' sx={{ pt: { xs: 2, sm: 3 } }}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={5.2}>
                <Typography color='text.primary'>
                    Configure system-wide rules to automatically place clients
                    on hold or cancel reservations when invoices remain unpaid.
                </Typography>

                <FormProvider {...methods}>
                    <form>
                        <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
                            <Typography
                                color='text.primary'
                                sx={{ fontWeight: 700, mb: { xs: 2, sm: 3 } }}
                            >
                                Automatic Credit Hold Settings
                            </Typography>

                            <Stack spacing={{ xs: 2, sm: 2.5 }}>
                                {/* Row: Automatic Credit Hold */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{
                                        xs: 'flex-start',
                                        sm: 'center',
                                    }}
                                    gap={1}
                                >
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        gap={1}
                                    >
                                        <InputLabel sx={{ m: 0 }}>
                                            Enable Automatic Credit Hold
                                        </InputLabel>
                                        <CustomTooltip
                                            title={
                                                <Typography component='div'>
                                                    <ul
                                                        style={{
                                                            paddingLeft: 18,
                                                            margin: 0,
                                                        }}
                                                    >
                                                        <li>
                                                            When enabled,
                                                            customers are
                                                            automatically placed
                                                            on credit hold after
                                                            their invoice has
                                                            been overdue for the
                                                            configured number of
                                                            days.
                                                        </li>
                                                        <li>
                                                            If enabled after
                                                            10am, clients with
                                                            an overdue invoice
                                                            will be placed on
                                                            credit hold at 10am
                                                            the following day.
                                                        </li>
                                                    </ul>
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: 16,
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Stack>

                                    <Box sx={{ flexGrow: 1 }} />
                                    <Controller
                                        name='automaticCreditHold'
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e.target.checked,
                                                    );
                                                    void handleSubmit(
                                                        onSubmit,
                                                    )();
                                                }}
                                            />
                                        )}
                                    />
                                </Stack>

                                {/* Row: Days overdue before credit hold */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{
                                        xs: 'flex-start',
                                        sm: 'center',
                                    }}
                                    gap={1}
                                >
                                    <Stack sx={{ flexGrow: 1 }}>
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            gap={1}
                                        >
                                            <InputLabel sx={{ m: 0 }}>
                                                Overdue Window
                                            </InputLabel>
                                            <CustomTooltip
                                                title={
                                                    <Typography>
                                                        How many days after an
                                                        invoice due date the
                                                        system waits before
                                                        placing a client on
                                                        automatic credit hold
                                                        (only applies if
                                                        automatic credit hold is
                                                        enabled).
                                                    </Typography>
                                                }
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: 16,
                                                    }}
                                                />
                                            </CustomTooltip>
                                        </Stack>

                                        {methods.formState.errors
                                            .daysOverdueBeforeAutomaticCreditHold && (
                                            <Typography
                                                variant='caption'
                                                color='error'
                                            >
                                                {
                                                    methods.formState.errors
                                                        .daysOverdueBeforeAutomaticCreditHold
                                                        .message
                                                }
                                            </Typography>
                                        )}
                                    </Stack>

                                    <FormTextField
                                        name='daysOverdueBeforeAutomaticCreditHold'
                                        control={control}
                                        type='number'
                                        placeholder='5'
                                        disabled={!watchCreditHold}
                                        size='small'
                                        hideError
                                        onBlur={() => {
                                            void handleSubmit(onSubmit)();
                                        }}
                                        sx={{
                                            width: { xs: '100%', sm: '8ch' },
                                            alignSelf: {
                                                xs: 'stretch',
                                                sm: 'auto',
                                            },
                                        }}
                                    />
                                </Stack>

                                {/* Row: Automatic Reservations Cancellation */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{
                                        xs: 'flex-start',
                                        sm: 'center',
                                    }}
                                    gap={1}
                                >
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        gap={1}
                                    >
                                        <InputLabel sx={{ m: 0 }}>
                                            Enable Automatic Cancellations
                                        </InputLabel>
                                        <CustomTooltip
                                            title={
                                                <Typography>
                                                    When enabled, future
                                                    reservations will be
                                                    cancelled a set number of
                                                    days after a client is
                                                    placed on credit hold.
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: 16,
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Stack>

                                    <Box sx={{ flexGrow: 1 }} />
                                    <Controller
                                        name='automaticBookingCancellation'
                                        control={control}
                                        render={({ field }) => (
                                            <Switch
                                                checked={field.value}
                                                onChange={(e) => {
                                                    field.onChange(
                                                        e.target.checked,
                                                    );
                                                    void handleSubmit(
                                                        onSubmit,
                                                    )();
                                                }}
                                            />
                                        )}
                                    />
                                </Stack>

                                {/* Row: Cancellation Window (Days) */}
                                {/* Row: Cancellation Window (Days) */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{
                                        xs: 'flex-start',
                                        sm: 'center',
                                    }}
                                    gap={1}
                                >
                                    {/* Label + tooltip + error */}
                                    <Stack sx={{ flexGrow: 1 }}>
                                        <Stack
                                            direction='row'
                                            alignItems='center'
                                            gap={1}
                                        >
                                            <InputLabel sx={{ m: 0 }}>
                                                Cancellation Window
                                            </InputLabel>
                                            <CustomTooltip
                                                title={
                                                    <Typography>
                                                        How many days the system
                                                        waits to cancel
                                                        reservations after a
                                                        client is placed on
                                                        credit hold (only
                                                        applies if automatic
                                                        cancellation is
                                                        enabled).
                                                    </Typography>
                                                }
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: 16,
                                                    }}
                                                />
                                            </CustomTooltip>
                                        </Stack>

                                        {/* Error under label */}
                                        {methods.formState.errors
                                            .daysBeforeBookingCancellation && (
                                            <Typography
                                                variant='caption'
                                                color='error'
                                            >
                                                {
                                                    methods.formState.errors
                                                        .daysBeforeBookingCancellation
                                                        .message
                                                }
                                            </Typography>
                                        )}
                                    </Stack>

                                    {/* Field aligned right */}
                                    <FormTextField
                                        name='daysBeforeBookingCancellation'
                                        control={control}
                                        type='number'
                                        placeholder='5'
                                        disabled={!watchCancel}
                                        size='small'
                                        hideError
                                        onBlur={() => {
                                            void handleSubmit(onSubmit)();
                                        }}
                                        sx={{
                                            width: { xs: '100%', sm: '8ch' },
                                            alignSelf: {
                                                xs: 'stretch',
                                                sm: 'auto',
                                            },
                                        }}
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    </form>
                </FormProvider>
            </Grid>
        </Grid>
    );
};

export default CreditHoldSettings;
